import { create } from "zustand";
import { getCountryList, getCountryListNew } from "../api/member";
import { type Country, CountryTypeEnum } from "../api/types/member";
import { RiskClass } from "../api/types/auth";

interface CountryState {
  countryList: Country[];
  allCountries: Country[];
  // 缓存各分类的数据
  hotCountries: Country[];
  lowRiskCountries: Country[];
  mediumRiskCountries: Country[];
  highRiskCountries: Country[];
  otherCountries: Country[];
  count: { [key: string]: any };
  loading: boolean;
  loaded: boolean;
  fetchCountryListByType: (type: CountryTypeEnum) => Promise<void>;
  fetchCountryList: (type?: CountryTypeEnum, keyword?: string) => Promise<void>;

  // Helpers
  getPhoneCodeOptions: () => Array<{ label: string; value: string }>;
  getCountryOptions: () => Array<{ label: string; value: string }>;
  getCountryCount: (type: CountryTypeEnum) => number;
  getHotCountries: () => Country[];
  getRiskCountries: (riskClass: RiskClass) => Country[];
  getOtherCountries: () => Country[];
}

export const useCountryStore = create<CountryState>((set, get) => ({
  countryList: [],
  allCountries: [],
  hotCountries: [],
  lowRiskCountries: [],
  mediumRiskCountries: [],
  highRiskCountries: [],
  otherCountries: [],
  count: {},
  loading: false,
  loaded: false,

  // 按需加载指定分类的国家数据
  fetchCountryListByType: async (type: CountryTypeEnum) => {
    const state = get();

    // 检查该分类是否已加载
    const getCachedData = (t: CountryTypeEnum): Country[] | null => {
      switch (t) {
        case CountryTypeEnum.Hot:
          return state.hotCountries.length > 0 ? state.hotCountries : null;
        case CountryTypeEnum.LowRisk:
          return state.lowRiskCountries.length > 0 ? state.lowRiskCountries : null;
        case CountryTypeEnum.MediumRisk:
          return state.mediumRiskCountries.length > 0 ? state.mediumRiskCountries : null;
        case CountryTypeEnum.HighRisk:
          return state.highRiskCountries.length > 0 ? state.highRiskCountries : null;
        case CountryTypeEnum.Other:
          return state.otherCountries.length > 0 ? state.otherCountries : null;
        default:
          return null;
      }
    };

    const cached = getCachedData(type);
    if (cached) {
      // 已有缓存，直接返回
      return;
    }

    set({ loading: true });
    try {
      const res = await getCountryListNew(type);
      const countries = res.countries || [];
      const count = res.count || {};

      // 更新对应分类的缓存
      const updates: Partial<CountryState> = {
        loading: false,
        count: { ...state.count, ...count }, // 合并 count 数据
      };

      switch (type) {
        case CountryTypeEnum.Hot:
          updates.hotCountries = countries;
          break;
        case CountryTypeEnum.LowRisk:
          updates.lowRiskCountries = countries;
          break;
        case CountryTypeEnum.MediumRisk:
          updates.mediumRiskCountries = countries;
          break;
        case CountryTypeEnum.HighRisk:
          updates.highRiskCountries = countries;
          break;
        case CountryTypeEnum.Other:
          updates.otherCountries = countries;
          break;
      }

      // 合并到 allCountries（去重）
      const allCountriesMap = new Map<string, Country>();
      state.allCountries.forEach((c) => {
        if (c.id) allCountriesMap.set(String(c.id), c);
      });
      countries.forEach((c) => {
        if (c.id) allCountriesMap.set(String(c.id), c);
      });

      updates.allCountries = Array.from(allCountriesMap.values());
      updates.loaded = true;

      set(updates);
    } catch (error) {
      console.error(`加載${type}分類國家列表失敗:`, error);
      set({ loading: false });
    }
  },

  fetchCountryList: async (type?: CountryTypeEnum, keyword?: string) => {
    // Scenario 1: Called with a specific type (from Modal) -> Use new API
    if (type) {
      set({ loading: true });
      try {
        const res = await getCountryListNew(type, keyword);
        set({ countryList: res.countries || [], count: res.count || {} });

      } catch (error) {
        console.error("加載國家列表失敗:", error);
        set({ countryList: [] });
      } finally {
        set({ loading: false });
      }

      // Ensure allCountries is also loaded for lookups if not already
      if (!get().loaded) {
        getCountryList()
          .then((res) => set({ allCountries: res, loaded: true }))
          .catch(console.error);
      }
      return;
    }

    const { loaded, allCountries } = get();

    // If not loaded, fetch all data once
    if (!loaded) {
      set({ loading: true });
      try {
        const res = await getCountryList(); // Fetch all
        set({ allCountries: res, loaded: true });
        // If no keyword, default countryList to all (or empty? usually we want all for select dropdown if it used countryList)
        // But CountrySelect usually uses allCountries directly or filtered locally?
        // Let's see... CountrySelect uses allCountries for lookup.
        // CountryCodeModal uses countryList for display.
        if (!keyword) {
          set({ countryList: res });
        }
      } catch (error) {
        console.error("加載國家列表失敗:", error);
        set({ loading: false });
        return;
      }
    }

    // Filter from allCountries based on keyword (Legacy/Local filtering)
    // Only if type is NOT provided (which we handled above)
    const currentAll = get().allCountries;
    let filtered = currentAll;

    if (keyword) {
      const lowerKey = keyword.toLowerCase();
      filtered = currentAll.filter(
        (c) => c.cname?.toLowerCase().includes(lowerKey) || c.name?.toLowerCase().includes(lowerKey) || c.countryCode?.toLowerCase().includes(lowerKey) || String(c.areaCode).includes(lowerKey)
      );
    }

    set({ countryList: filtered, loading: false });
  },

  getPhoneCodeOptions: () => {
    const { countryList } = get();
    // Filter out entries without areaCode and deduplicate
    const uniqueCodes = new Map();
    countryList.forEach((c) => {
      if (c.areaCode && !uniqueCodes.has(c.areaCode)) {
        uniqueCodes.set(c.areaCode, {
          label: `+${c.areaCode}`,
          value: c.areaCode,
        });
      }
    });

    // Sort by code roughly (optional)
    return Array.from(uniqueCodes.values()).sort((a, b) => parseInt(a.value) - parseInt(b.value));
  },

  getCountryOptions: () => {
    const { countryList } = get();
    return countryList
      .filter((c) => c.countryCode)
      .map((c) => ({
        label: c.cname || c.name || c.countryCode || "",
        value: c.countryCode || "",
      }));
  },

  getCountryCount: (type: CountryTypeEnum) => {
    const { count } = get();
    return count[type] || 0;
  },

  getHotCountries: () => {
    const { countryList } = get();
    return countryList.filter((c) => c.hot);
  },

  getRiskCountries: (riskClass: RiskClass) => {
    const { countryList } = get();
    return countryList.filter((c) => c.riskClass === riskClass);
  },

  getOtherCountries: () => {
    const { countryList } = get();
    return countryList.filter((c) => !c.hot && !c.riskClass);
  },
}));
